import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  useTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../dashboard/theme";
import NavigationHeader from "../components/NavigationHeader";
import axios from "axios";
import { ReactSession } from "react-client-session";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import config from "./global/config";

const Permissions = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    severity: "success",
    message: "",
  });

  const api = axios.create({
    baseURL: config.apiUrl,
  });

  // Add token to requests
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Fetch all available pages
  const fetchAvailablePages = async () => {
    try {
      const response = await api.get("pages.php");
      return response.data;
    } catch (error) {
      console.error("Error fetching pages:", error);
      showAlert("error", "Failed to fetch pages");
      return [];
    }
  };

  // Fetch profiles
  const fetchProfiles = async () => {
    try {
      const response = await api.get("profiles.php");
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      showAlert("error", "Failed to fetch profiles");
    }
  };

  // Convert string or number to boolean
  const toBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string")
      return value === "1" || value.toLowerCase() === "true";
    if (typeof value === "number") return value === 1;
    return false;
  };

  // Fetch permissions and update available pages
  const fetchPermissions = async (profileName) => {
    try {
      setLoading(true);
      const [permissionsResponse, allPages] = await Promise.all([
        api.get(`permissions.php?profile=${profileName}`),
        fetchAvailablePages(),
      ]);

      const formattedPermissions = permissionsResponse.data.map(
        (permission) => ({
          id: permission.idAPU,
          page: permission.page,
          visibility: toBool(permission.visibility),
          op_add: toBool(permission.op_add),
          op_edit: toBool(permission.op_edit),
          op_delete: toBool(permission.op_delete),
        })
      );

      // Filter out pages that are already assigned
      const assignedPages = new Set(formattedPermissions.map((p) => p.page));
      const unassignedPages = allPages.filter(
        (page) => !assignedPages.has(page.page)
      );

      setPermissions(formattedPermissions);
      setAvailablePages(unassignedPages);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      showAlert("error", "Failed to fetch permissions");
    } finally {
      setLoading(false);
    }
  };

  // Add new pages to profile
  const handleAddPages = async () => {
    try {
      setLoading(true);
      const addPromises = selectedPages.map((page) => {
        const formData = new FormData();
        formData.append("profile", selectedProfile);
        formData.append("page", page);
        formData.append("visibility", "0");
        formData.append("op_add", "0");
        formData.append("op_edit", "0");
        formData.append("op_delete", "0");
        formData.append("user_cre", ReactSession.get("username"));
        formData.append("METHOD", "POST");

        return api.post("permissions.php", formData);
      });

      await Promise.all(addPromises);
      showAlert("success", "Pages added successfully");
      setOpenDialog(false);
      setSelectedPages([]);
      await fetchPermissions(selectedProfile);
    } catch (error) {
      console.error("Error adding pages:", error);
      showAlert("error", "Failed to add pages");
    } finally {
      setLoading(false);
    }
  };

  // Save permissions
  const handleSavePermissions = async () => {
    try {
      setLoading(true);
      const savePromises = permissions.map((permission) => {
        const formData = new FormData();
        formData.append("visibility", permission.visibility ? "1" : "0");
        formData.append("op_add", permission.op_add ? "1" : "0");
        formData.append("op_edit", permission.op_edit ? "1" : "0");
        formData.append("op_delete", permission.op_delete ? "1" : "0");
        formData.append("user_upd", ReactSession.get("username"));
        formData.append("METHOD", "PUT");

        return api.post(`permissions.php?idAPU=${permission.id}`, formData);
      });

      await Promise.all(savePromises);
      showAlert("success", "Permissions updated successfully");
    } catch (error) {
      console.error("Error saving permissions:", error);
      showAlert("error", "Failed to save permissions");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (severity, message) => {
    setAlert({
      show: true,
      severity,
      message,
    });
    setTimeout(
      () => setAlert({ show: false, severity: "", message: "" }),
      3000
    );
  };

  const handleProfileChange = (event) => {
    const profileName = event.target.value;
    setSelectedProfile(profileName);
    if (profileName) {
      fetchPermissions(profileName);
    } else {
      setPermissions([]);
      setAvailablePages([]);
    }
  };

  const handlePermissionChange = (id, field, newValue) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((permission) =>
        permission.id === id ? { ...permission, [field]: newValue } : permission
      )
    );
  };

  const columns = [
    { field: "page", headerName: "Page", flex: 1 },
    {
      field: "visibility",
      headerName: "View",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.visibility}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "visibility",
              e.target.checked
            )
          }
        />
      ),
    },
    {
      field: "op_add",
      headerName: "Add",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.op_add}
          onChange={(e) =>
            handlePermissionChange(params.row.id, "op_add", e.target.checked)
          }
        />
      ),
    },
    {
      field: "op_edit",
      headerName: "Edit",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.op_edit}
          onChange={(e) =>
            handlePermissionChange(params.row.id, "op_edit", e.target.checked)
          }
        />
      ),
    },
    {
      field: "op_delete",
      headerName: "Delete",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.op_delete}
          onChange={(e) =>
            handlePermissionChange(params.row.id, "op_delete", e.target.checked)
          }
        />
      ),
    },
  ];

  useEffect(() => {
    fetchProfiles();
  }, []);

  return (
    <Box m="25px">
      <NavigationHeader
        title="Permissions"
        subtitle="Profile Permissions Management"
      />

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: colors.primary[900] }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Select Profile</InputLabel>
              <Select
                value={selectedProfile}
                label="Select Profile"
                onChange={handleProfileChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {profiles.map((profile) => (
                  <MenuItem key={profile.profile} value={profile.profile}>
                    {profile.profile}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<SaveIcon />}
              onClick={handleSavePermissions}
              disabled={!selectedProfile || loading}
            >
              Save Changes
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              disabled={
                !selectedProfile || loading || availablePages.length === 0
              }
            >
              Add Pages
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          height: "calc(100vh - 300px)",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={permissions}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={loading}
          />
        )}
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add Pages to Profile</DialogTitle>
        <DialogContent>
          <FormControl sx={{ m: 1, minWidth: 300 }}>
            <InputLabel>Select Pages</InputLabel>
            <Select
              multiple
              value={selectedPages}
              onChange={(e) => setSelectedPages(e.target.value)}
              label="Select Pages"
            >
              {availablePages.map((page) => (
                <MenuItem key={page.page} value={page.page}>
                  {page.page}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddPages}
            variant="contained"
            color="secondary"
            disabled={selectedPages.length === 0}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions;
